/**
 * RegenerationPanel - Panneau pour régénérer des articles avec un nouvel angle
 * Extrait de CreateArticle.js pour améliorer la maintenabilité
 */
import React from 'react';
import { RefreshCw, CheckCircle, Loader2, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';

/**
 * Carte compacte de site pour la régénération (double colonne)
 */
const RegenSiteCard = ({
  siteId,
  siteName,
  siteUrl,
  isSelected,
  alreadyHasV2,
  hasV1,
  angle,
  title,
  onToggle,
  onAngleChange,
  onTitleChange,
  isNewSite = false
}) => (
  <div 
    className={`p-2.5 rounded-lg border transition-all ${
      alreadyHasV2 
        ? 'bg-slate-800/30 border-slate-700/50 opacity-50 cursor-not-allowed' 
        : isSelected 
          ? 'bg-amber-500/10 border-amber-500/40 shadow-sm shadow-amber-500/10' 
          : 'bg-slate-900/40 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/40'
    }`}
  >
    <label className={`flex items-center gap-2.5 ${alreadyHasV2 ? '' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={isSelected}
        disabled={alreadyHasV2}
        onChange={(e) => onToggle(e.target.checked)}
        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/50 focus:ring-offset-0"
        data-testid={`regen-checkbox-${siteId}`}
      />
      <img 
        src={`https://www.google.com/s2/favicons?domain=${siteUrl || ''}&sz=32`}
        alt="" 
        className="w-5 h-5 rounded flex-shrink-0"
      />
      <span className={`text-sm font-medium truncate ${
        alreadyHasV2 ? 'text-slate-500' : isSelected ? 'text-amber-300' : 'text-slate-300'
      }`}>
        {siteName}
      </span>
      {alreadyHasV2 && (
        <span className="text-[9px] bg-slate-700/80 text-slate-400 px-1.5 py-0.5 rounded ml-auto flex-shrink-0">
          v2 fait
        </span>
      )}
      {!alreadyHasV2 && hasV1 && (
        <span className="text-[9px] text-amber-500/70 ml-auto flex-shrink-0">
          v1
        </span>
      )}
      {!alreadyHasV2 && isNewSite && (
        <Plus className="w-3.5 h-3.5 text-emerald-400/70 ml-auto flex-shrink-0" />
      )}
    </label>
    
    {/* Champs angle et titre (visible si sélectionné) */}
    {isSelected && !alreadyHasV2 && (
      <div className="mt-2.5 space-y-2 pl-6">
        <div>
          <label className="text-[10px] text-amber-400/80 uppercase tracking-wider">
            Angle / Instructions
          </label>
          <Textarea
            value={angle || ''}
            onChange={(e) => onAngleChange(e.target.value)}
            placeholder="Ex: Insister sur l'impact économique..."
            className="mt-1 bg-slate-950/50 border-amber-500/30 text-slate-200 text-xs min-h-[50px] placeholder:text-slate-600 focus:border-amber-500/50"
            data-testid={`regen-angle-${siteId}`}
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase tracking-wider">
            Titre (optionnel)
          </label>
          <Input
            value={title || ''}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Laisser vide = auto"
            className="mt-1 bg-slate-950/50 border-slate-700 text-slate-200 text-xs placeholder:text-slate-600"
          />
        </div>
      </div>
    )}
  </div>
);

/**
 * Panneau principal de régénération
 */
const RegenerationPanel = ({
  showPanel,
  onTogglePanel,
  compendiumStats,
  generatedArticles,
  sites,
  regenSites,
  onRegenSiteChange,
  onRegenerate,
  regenerating,
  verboseLog = []
}) => {
  const selectedCount = Object.values(regenSites).filter(s => s.selected).length;
  const logContainerRef = React.useRef(null);
  
  // Auto-scroll vers le bas quand les logs changent
  React.useEffect(() => {
    if (logContainerRef.current && regenerating) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [verboseLog, regenerating]);
  
  // Sites déjà générés (peuvent être régénérés)
  const existingSites = Object.entries(generatedArticles || {})
    .filter(([id]) => !id.startsWith('extra_'))
    .map(([siteId, articleData]) => {
      const site = sites.find(s => s.id === siteId);
      return {
        siteId,
        siteName: site?.name || articleData?.site_name || siteId,
        siteUrl: site?.url || '',
        alreadyHasV2: articleData?.v2 || articleData?.version === 2,
        hasV1: true
      };
    });
  
  // Tous les sites disponibles qui n'ont pas encore d'article généré
  const newSites = sites
    .filter(s => !generatedArticles?.[s.id])
    .map(site => ({
      siteId: site.id,
      siteName: site.name,
      siteUrl: site.url,
      alreadyHasV2: false,
      hasV1: false,
      isNew: true
    }));

  // Combiner tous les sites pour l'affichage en grille
  const allSites = [...existingSites, ...newSites];

  return (
    <Card className="relative bg-slate-950/40 backdrop-blur-xl border-amber-500/30" data-testid="regen-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-mono uppercase text-amber-400 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Continuer cette mission
          </CardTitle>
          <button
            onClick={onTogglePanel}
            className="text-xs text-slate-400 hover:text-amber-400 transition-colors px-2 py-1 rounded border border-slate-700 hover:border-amber-500/50"
          >
            {showPanel ? 'Masquer' : 'Afficher'}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          Régénérez avec un angle différent ou ajoutez des sites oubliés — sans relancer la recherche.
        </p>
      </CardHeader>
      
      {showPanel && (
        <CardContent className="space-y-3 pt-2">
          {/* Indicateur de sources conservées */}
          {compendiumStats && (
            <div className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[11px] text-emerald-400">
                Sources conservées : {compendiumStats.facts} faits, {compendiumStats.figures} chiffres
              </span>
            </div>
          )}
          
          {/* Grille des sites en 2 colonnes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {allSites.map(({ siteId, siteName, siteUrl, alreadyHasV2, hasV1, isNew }) => (
              <RegenSiteCard
                key={siteId}
                siteId={siteId}
                siteName={siteName}
                siteUrl={siteUrl}
                isSelected={regenSites[siteId]?.selected || false}
                alreadyHasV2={alreadyHasV2}
                hasV1={hasV1}
                angle={regenSites[siteId]?.angle}
                title={regenSites[siteId]?.title}
                onToggle={(checked) => onRegenSiteChange(siteId, { selected: checked })}
                onAngleChange={(angle) => onRegenSiteChange(siteId, { angle })}
                onTitleChange={(title) => onRegenSiteChange(siteId, { title })}
                isNewSite={isNew}
              />
            ))}
          </div>
          
          {/* Logs verbose pendant la régénération */}
          {regenerating && verboseLog && verboseLog.length > 0 && (
            <div 
              ref={logContainerRef}
              className="mb-3 p-3 bg-slate-950/70 border border-amber-500/20 rounded-lg max-h-32 overflow-y-auto"
            >
              <p className="text-[10px] text-amber-400/70 uppercase tracking-wider mb-2">Progression</p>
              <div className="space-y-1 font-mono text-[11px]">
                {verboseLog.slice(-8).map((log, idx) => (
                  <div key={idx} className="text-slate-400 leading-tight">
                    {log.includes('ERREUR') ? (
                      <span className="text-red-400">{log}</span>
                    ) : log.includes('TERMINÉ') ? (
                      <span className="text-emerald-400">{log}</span>
                    ) : log.includes('GÉNÉRATION') ? (
                      <span className="text-amber-300">{log}</span>
                    ) : (
                      <span>{log}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Bouton de régénération */}
          <div className="pt-2 border-t border-slate-800/50">
            <Button
              onClick={onRegenerate}
              disabled={regenerating || selectedCount === 0}
              className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/50 font-semibold py-2.5 disabled:opacity-40"
              data-testid="regen-btn"
            >
              {regenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Régénération en cours...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Régénérer ({selectedCount} site{selectedCount > 1 ? 's' : ''})
                </>
              )}
            </Button>
            <p className="text-[10px] text-slate-600 mt-1.5 text-center">
              Les nouveaux articles s&apos;afficheront à côté des originaux (v1 / v2)
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default RegenerationPanel;
